import { Helmet } from 'react-helmet';

import { GOOGLE_OPTIMIZE_SCRIPT, ANTI_FLICKER_SCRIPT } from '~utils/scripts';
import { GOOGLE_OPTIMIZE_ENABLED } from '~constants/features';

const gtmId = process.env.REACT_APP_WEBSITE_NAME;
const isValidGtmId = typeof gtmId === 'string' && /^GTM-[A-Z0-9]+$/.test(gtmId);

const tagManagerArgs = {
  gtmId
};

export const renderHeadAnalyticsScripts = () => (
  <Helmet>
    {GOOGLE_OPTIMIZE_ENABLED && <script>{GOOGLE_OPTIMIZE_SCRIPT}</script>}
    {GOOGLE_OPTIMIZE_ENABLED && <script>{ANTI_FLICKER_SCRIPT}</script>}
  </Helmet>
);

// GTM is the entry point for TikTok, Meta, Clarity, Hotjar, barilliance and the ad tags:
// 1.4MB and 2.7s of main-thread work measured on mobile. `requestIdleCallback` never finds
// an idle slot while the app is booting, so its 3000ms timeout was firing on the deadline —
// at ~3s, with FCP at 5.1s. The tags were landing in the middle of the first render and
// competing with it for the main thread. Waiting for `load` keeps them off the critical
// path entirely: by then the banner, which is the LCP element, has already painted.
const ANALYTICS_IDLE_TIMEOUT = 2000;
// A stalled image or third-party request must not hold the tags back forever.
const ANALYTICS_MAX_DELAY = 8000;

export const loadAnalyticsScripts = () => {
  const initAnalytics = () => {
    import('react-gtm-module')
      .then(({ default: TagManager }) => {
        const isLocalhost =
          typeof window !== 'undefined' &&
          ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

        if (!isLocalhost && isValidGtmId) {
          TagManager?.initialize(tagManagerArgs);
        }
      })
      .catch(() => null);
  };

  let started = false;
  const start = () => {
    if (started) {
      return;
    }
    started = true;

    // Yield one more slot so the tags never share a frame with the post-load render
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(initAnalytics, { timeout: ANALYTICS_IDLE_TIMEOUT });
      return;
    }

    setTimeout(initAnalytics, 200);
  };

  setTimeout(start, ANALYTICS_MAX_DELAY);

  if (document.readyState === 'complete') {
    start();
    return;
  }

  window.addEventListener('load', start, { once: true });
};

export const hasAnalyticsConsent = () =>
  window.Cookiebot && window.Cookiebot.consents && window.Cookiebot.consents.analytics === true;
